import React, { useState, useEffect } from 'react';
import { CModal, CModalBody, CButton, CForm } from '@coreui/react-pro';
import Waiting from '../../../../General/Loadings/Waiting';
import { LOCAL_ORG_USER, POPUP_TYPE, TRAINING_PRODUCT_TYPES } from '../../../../../constants';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import { SimpleInputGroup } from '../../../../General/Forms';
import { toast } from 'react-toastify';
import { checkCeProductIsRPT, jsonAndCompare, tryParseJSON, validateEmail } from '../../../../../utils';
import { assignSeatRequest } from './actions';
import { setShow, setShowPopupDetail } from '../../../../../actions/general';
import images from '../../../../../assets/images';
import { fetchSubscriberProductsRequest } from '../../../Subscriber/Products/actions';
import { useParams } from 'react-router-dom';
import { useActiveProduct } from '../../../../../helpers/customHook';
import AssignSeatUserSearch from '../AssignSeatUserSearch';

const AssignBundleSeat = ({ productId, bundleSeatId, bundleSeats, organizationMembers, fetchOrgSeats, setPagination }) => {
     const { courseId } = useParams();
     const dispatch = useDispatch();
     const organizationMemberUsers = useSelector((state) => state.subscriber.organizationMembers);
     const products = useSelector((state) => state.subscriber.products.list);
     const product = useActiveProduct(productId);

     const productsInBundleRaw = tryParseJSON(product?.listBundleProduct) || [];
     const productsInBundleIds = productsInBundleRaw.map((product) => product.id);
     const productsInBundle = products.filter((product) => productsInBundleIds.includes(product.id));

     const [validateOnChange, setValidateOnChange] = useState(false);

     const orgUserLocalStorage = tryParseJSON(localStorage.getItem(LOCAL_ORG_USER));

     const fetchProducts = () => {
          if (!products.length) {
               dispatch(fetchSubscriberProductsRequest());
          }
     };

     useEffect(() => {
          fetchProducts();
     }, []); // eslint-disable-line react-hooks/exhaustive-deps

     const initialValues = {
          userId: '',
          email: '',
     };

     const newListMembers = organizationMembers?.map((member) => ({
          value: member.id,
          label: member.email,
     }));

     const validationSchema = (values) => {
          const errors = {};
          setValidateOnChange(true);

          if (!values.email && !values.userId) {
               errors.email = 'Email address is required.';
               errors.userId = 'Email address is required.';
          }

          if (values.email && !validateEmail(values.email)) {
               errors.email = 'Not a valid email';
          }

          return errors;
     };

     const handleFormSubmit = ({ email, userId }, { setFieldError, setSubmitting }) => {
          if (!email && !userId) {
               setSubmitting(false);
               toast.error('Email address is required');
               return;
          }

          if (email && userId) {
               setSubmitting(false);
               toast.error('Only an email is allowed');
               return;
          }

          const mgsEmailInOrgMember =
               email && organizationMemberUsers?.some((orgMember) => orgMember.email === email) && 'User ready existing on this organization.';

          if (mgsEmailInOrgMember) {
               setFieldError('email', mgsEmailInOrgMember);

               setSubmitting(false);
               return;
          }

          const memberEmail = newListMembers.find((orgMember) => orgMember.value === userId)?.label || email || '';

          const isCheckExistByProductType = productsInBundle.every((product) => {
               const productType = product?.productType?.trainingProductType;
               const isPhysical = productType === TRAINING_PRODUCT_TYPES.PHYSICAL_PRODUCT;
               const isCECredit = productType === TRAINING_PRODUCT_TYPES.CE_CREDITS;
               const isLiveTraining = productType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING;
               const isEventCredit = isLiveTraining && product.allowPurchaseEventCredit;

               return !isPhysical && !isCECredit && !isEventCredit;
          });

          const emailExistsInSeats =
               isCheckExistByProductType &&
               bundleSeats.some((seatItem) => {
                    const product = products.find((prod) => prod.id === seatItem.productId);

                    const checkCeProduct = product && checkCeProductIsRPT(product, products);
                    const isCorrectMail = seatItem.email?.toLowerCase() === memberEmail.toLowerCase();
                    const isNotExpired = !seatItem.expiredAt || new Date().getTime() <= new Date(seatItem.expiredAt).getTime();

                    return isCorrectMail && !checkCeProduct && isNotExpired;
               });

          if (emailExistsInSeats) {
               setSubmitting(false);
               toast.error('User already has a seat at this product.');
               return;
          }

          const newValues = {
               organizationId: orgUserLocalStorage.organizationId,
               organizationProductId: courseId,
               bundleSeatIds: [bundleSeatId],
               emails: email ? [email.toLowerCase()] : [memberEmail.toLowerCase()],
          };

          const nextDispatch = {
               setValidateOnChange,
               setSubmitting,
               fetchOrgSeats,
               setPagination,
          };

          dispatch(assignSeatRequest(newValues, nextDispatch));
     };

     const handleCancelButtonClick = () => {
          dispatch(setShowPopupDetail(null));
     };

     const handleOpenBulkAddUser = () => {
          dispatch(setShowPopupDetail(null));
          dispatch(setShow(POPUP_TYPE.ASSIGN_MULTIPLE_USERS));
     };

     return (
          <>
               <CModal alignment="center" visible={true} className="assign-seat-popup" scrollable>
                    <CModalBody>
                         <div className="heading">
                              <div className="icon-group">
                                   <img src={images.IconManPeoplePersonUser} className="icon-item" alt="Assign Seat" />
                                   <img src={images.IconAuthorizeArrowRight} className="icon-item" alt="Arrow Seat" />
                                   <img src={images.IconShape} className="icon-item" alt="Icon Seat" />
                              </div>
                              <h4 className="card-title">Assign Seat</h4>
                              <p>Select a user to assign this seat to</p>
                         </div>
                         <Formik
                              initialValues={initialValues}
                              onSubmit={handleFormSubmit}
                              validate={validationSchema}
                              validateOnChange={validateOnChange}
                              validateOnBlur={false}
                         >
                              {({ values, errors, setFieldValue, handleChange, handleSubmit, isSubmitting }) => {
                                   const memberValue = newListMembers.find((orgMember) => orgMember.value === values.userId)?.label || '';
                                   const isDisabled = values.userId && jsonAndCompare(initialValues, values);

                                   return (
                                        <CForm onSubmit={handleSubmit} noValidate>
                                             <AssignSeatUserSearch
                                                  initialValues={newListMembers}
                                                  value={memberValue}
                                                  error={errors.userId}
                                                  disabled={isSubmitting}
                                                  setFieldValue={setFieldValue}
                                             />
                                             <div className="d-flex justify-content-center symbol-or">or</div>
                                             <SimpleInputGroup
                                                  className="mr-2"
                                                  classNameInput="new-user-email"
                                                  id="email"
                                                  placeholder="Invite a new user, enter their email"
                                                  value={values.email}
                                                  error={errors.email}
                                                  handleChange={(e) => setFieldValue('email', e.target.value.trim())}
                                                  disabled={isSubmitting}
                                                  isValidate
                                             />
                                             <div className="mb-3">
                                                  <CButton
                                                       type="submit"
                                                       className="me-3 text-uppercase invite-user"
                                                       disabled={isSubmitting || !!isDisabled}
                                                  >
                                                       <Waiting isLoading={isSubmitting}>Invite User</Waiting>
                                                  </CButton>
                                             </div>
                                             <div className="text-center">
                                                  <button type="button" className="bulk-add-user" onClick={handleOpenBulkAddUser}>
                                                       Bulk Add User
                                                  </button>
                                             </div>
                                             <div className="text-center">
                                                  <button
                                                       type="button"
                                                       className="cancel-invite"
                                                       onClick={() => {
                                                            setFieldValue('userId', '');
                                                            handleCancelButtonClick();
                                                       }}
                                                  >
                                                       Cancel
                                                  </button>
                                             </div>
                                        </CForm>
                                   );
                              }}
                         </Formik>
                    </CModalBody>
               </CModal>
          </>
     );
};

export default AssignBundleSeat;
